import { emptyArray } from "constants/defaultValues";
import * as R from "@totalsoft/rules-algebra";
import * as V from "@totalsoft/pure-validations";
import { all, range } from "ramda";
import moment, { months } from "moment";
import { isValidDuration } from "utils/simple-validations/validations";
import { countryCodeConstants, leasingFactorTypeCodes } from "features/common/constants";
import { rvCalculationCodeConstants, scheduleTypeCellParams, serviceCalculationTypeId } from "./constants";

export const getPriceCardMatrixLine = (quoteProduct) => {
  const { priceCard, duration, totalAmount, totalAmountWithVAT, showAmountsWithVAT } = quoteProduct;

  if (!priceCard?.matrixLines?.length) return null;

  const amount = showAmountsWithVAT ? totalAmountWithVAT : totalAmount;
  const matrixLine = priceCard.matrixLines.find((line) => {
    const purchasePriceFrom = line?.purchasePriceFrom || 0;
    const purchasePriceTo = line?.purchasePriceTo || amount;
    return (
      duration >= line.tenorFrom && duration <= line.tenorTo && amount > purchasePriceFrom && amount <= purchasePriceTo
    );
  });

  return matrixLine;
};

export const getCustomerInterestPercent = (product, priceCardMatrixLine) => {
  if (!priceCardMatrixLine) return 0;
  const ratingInterestValue = product?.priceCard?.allowRiskBasePricing ? product.ratingBasedInterestPercent || 0 : 0;

  if (!priceCardMatrixLine.customerInterestPercent) {
    return product.interestMinPercent + ratingInterestValue;
  } else {
    if (product.interestTypeIsFixed) {
      return priceCardMatrixLine.customerInterestPercent;
    } else {
      return (
        priceCardMatrixLine.customerInterestPercent +
        product.bankMargin +
        product.floatingInterestPercent +
        ratingInterestValue
      );
    }
  }
};

export const getRatingBasedInterestPercent = (product) => {
  if (!product?.quoteRating) return 0;
  const ratingInterestValue = product?.priceCard?.ratingBasedPricings?.find((r) => r.rating === product.quoteRating)
    ?.interest;
  return ratingInterestValue ?? 0;
};

export const getServiceMinPercent = (amount, service, productServiceTypes) => {
  const dictionaryServiceType = productServiceTypes?.find((x) => x.serviceTypeId === service.serviceTypeId);
  if (!dictionaryServiceType) return 0;
  const minPercent = dictionaryServiceType.minPercent !== null ? dictionaryServiceType.minPercent : 0;
  return minPercent;
};

export const getServiceMaxPercent = (amount, service, productServiceTypes) => {
  const dictionaryServiceType = productServiceTypes?.find((x) => x.serviceTypeId === service.serviceTypeId);
  if (!dictionaryServiceType) return 100;

  const maxPercent =
    dictionaryServiceType.maxPercent !== null
      ? dictionaryServiceType.maxPercent
      : dictionaryServiceType.maxValue !== null
      ? (dictionaryServiceType.maxValue / amount) * 100
      : 100;

  return maxPercent;
};

export const getServiceMinAmount = (amount, service, productServiceTypes) => {
  const dictionaryServiceType = productServiceTypes?.find((x) => x.serviceTypeId === service.serviceTypeId);
  if (!dictionaryServiceType) return 0;
  const serviceMinAmount =
    dictionaryServiceType.minValue !== null
      ? dictionaryServiceType.minValue
      : dictionaryServiceType.minPercent !== null
      ? (dictionaryServiceType.minPercent / 100) * amount
      : 0;
  return serviceMinAmount;
};

export const getServiceMaxAmount = (amount, service, productServiceTypes) => {
  const dictionaryServiceType = productServiceTypes?.find((x) => x.serviceTypeId === service.serviceTypeId);
  if (!dictionaryServiceType) return amount;
  const serviceMaxAmount =
    dictionaryServiceType.maxValue !== null
      ? dictionaryServiceType.maxValue
      : dictionaryServiceType.maxPercent !== null
      ? (dictionaryServiceType.maxPercent / 100) * amount
      : amount;
  return serviceMaxAmount;
};

export const computeServiceAmount = (servicePercent, totalAmount, financedAmount, service, productServiceTypes) => {
  let baseAmount;
  const { calcTypeId } = service;

  if (calcTypeId === serviceCalculationTypeId.FinancedValuePercent) baseAmount = financedAmount;
  else baseAmount = totalAmount;

  let newAmount = (baseAmount * servicePercent) / 100 || 0;
  const serviceMinAmount = getServiceMinAmount(baseAmount, service, productServiceTypes);
  const serviceMaxAmount = getServiceMaxAmount(baseAmount, service, productServiceTypes);

  if (newAmount < serviceMinAmount) return serviceMinAmount;
  if (newAmount > serviceMaxAmount) return serviceMaxAmount;

  return newAmount;
};

export const computeServicePercent = (serviceAmount, totalAmount, financedAmount, service, productServiceTypes) => {
  let baseAmount;
  const { calcTypeId } = service;

  if (calcTypeId === serviceCalculationTypeId.FinancedValuePercent) baseAmount = financedAmount;
  else baseAmount = totalAmount;

  let newPercent = (100 * serviceAmount) / baseAmount || 0;
  const serviceMinPercent = getServiceMinPercent(baseAmount, service, productServiceTypes);
  const serviceMaxPercent = getServiceMaxPercent(baseAmount, service, productServiceTypes);

  if (newPercent < serviceMinPercent) return serviceMinPercent;
  if (newPercent > serviceMaxPercent) return serviceMaxPercent;

  return newPercent;
};

export const getResidualValueDefaultPercent = (product) => {
  const matrixLine = getPriceCardMatrixLine(product);
  const newResidualValuePercent =
    product.residualValueDefaultPercent <= (matrixLine?.residualValuePercent || 0)
      ? product.residualValueDefaultPercent
      : matrixLine?.residualValuePercent || 0;
  return newResidualValuePercent || 0;
};

export const getResidualValueDefaultAmount = (product) => {
  const percent = getResidualValueDefaultPercent(product);
  const customTotalAmount = product.showAmountsWithVAT ? product.totalAmountWithVAT : product.totalAmount;
  return (customTotalAmount * percent) / 100 || 0;
};

export const getInterestMinPercent = (product) => {
  const priceCardMatrixLine = getPriceCardMatrixLine(product);
  if (!priceCardMatrixLine) return product.interestMinPercent;
  const ratingInterestValue = product?.priceCard?.allowRiskBasePricing ? product.ratingBasedInterestPercent || 0 : 0;
  // const customerInterestPercent = getCustomerInterestPercent(product, priceCardMatrixLine);
  // return product.flatRate
  //   ? product.interestMinPercent
  //   : Math.max(product.interestMinPercent, customerInterestPercent - priceCardMatrixLine.subsidyPercent);
  return product.interestMinPercent + ratingInterestValue; // ERPFINSLFO-37390  //ERPFINSLFO-39371
};

export const getInterestMaxPercent = (product) => {
  return product.interestMaxPercent;
};

export const getProductValidator = (settings) =>
  V.fromModel(() => {
    return V.shape({
      quoteProductServices: V.items(
        V.fromModel((s) => {
          const serviceSettings = settings.quoteProductServices.find((x) => x.serviceTypeId === s.serviceTypeId);
          return V.shape({
            serviceAmount: V.between(serviceSettings.serviceMinAmount, serviceSettings.serviceMaxAmount),
            servicePercent: V.between(serviceSettings.serviceMinPercent, serviceSettings.serviceMaxPercent),
          });
        })
      ),
      duration: isValidDuration(settings),
      totalInterestPercent: V.between(settings.interestMinPercent, settings.interestMaxPercent),
      residualValuePercent: V.fromRoot((root) =>
        V.between(root.residualValueMinPercent, root.residualValueMaxPercentComputed)
      ),
      residualValueAmount: V.fromRoot(
        (root) =>
          V.between(
            (root.totalAmount * root.residualValueMinPercent) / 100,
            (root.totalAmount * root.residualValueMaxPercentComputed) / 100
          ) |> V.when(!root.showAmountsWithVAT)
      ),
      residualValueAmountWithVAT: V.fromRoot(
        (root) =>
          V.between(
            (root.totalAmountWithVAT * root.residualValueMinPercent) / 100,
            (root.totalAmountWithVAT * root.residualValueMaxPercentComputed) / 100
          ) |> V.when(root.showAmountsWithVAT)
      ),
      downPaymentPercent: V.fromRoot((root) => V.between(root.downPaymentMinPercent, root.downPaymentMaxPercent)),
      downPaymentAmount: V.fromRoot(
        (root) =>
          V.between(
            (root.totalAmount * root.downPaymentMinPercent) / 100,
            (root.totalAmount * root.downPaymentMaxPercent) / 100
          ) |> V.when(!root.showAmountsWithVAT)
      ),
      downPaymentAmountWithVAT: V.fromRoot(
        (root) =>
          V.between(
            (root.totalAmountWithVAT * root.downPaymentMinPercent) / 100,
            (root.totalAmountWithVAT * root.downPaymentMaxPercent) / 100
          ) |> V.when(root.showAmountsWithVAT)
      ),
    });
  });

export const getProductRules = (productServiceTypes) => {
  return R.fromModel((productModel) =>
    R.shape({
      quoteProductServices: R.items(
        R.shape({
          serviceAmount:
            R.computed((service) => {
              if (service.calcTypeId === serviceCalculationTypeId.ExactValue) {
                return service.serviceAmount;
              }
              const totalAmount = productModel.showAmountsWithVAT
                ? productModel.totalAmountWithVAT
                : productModel.totalAmount;

              const financedAmount = productModel.showAmountsWithVAT
                ? productModel.totalAmountWithVAT - productModel.downPaymentAmountWithVAT
                : productModel.totalAmount - productModel.downPaymentAmount;

              const newAmount = computeServiceAmount(
                service.servicePercent,
                totalAmount,
                financedAmount,
                service,
                productServiceTypes
              );
              return newAmount;
            }) |> R.when(R.propertiesChanged((m) => [m.servicePercent])),
          servicePercent:
            R.computed((service) => {
              if (service.calcTypeId === serviceCalculationTypeId.ExactValue) {
                return service.servicePercent;
              }
              const totalAmount = productModel.showAmountsWithVAT
                ? productModel.totalAmountWithVAT
                : productModel.totalAmount;

              const financedAmount = productModel.showAmountsWithVAT
                ? productModel.totalAmountWithVAT - productModel.downPaymentAmountWithVAT
                : productModel.totalAmount - productModel.downPaymentAmount;

              const newPercent = computeServicePercent(
                service.serviceAmount,
                totalAmount,
                financedAmount,
                service,
                productServiceTypes
              );
              return newPercent;
            }) |> R.when(R.propertiesChanged((m) => [m.serviceAmount])),
        })
      ),
      residualValueVisible: R.when(
        R.propertyChanged((product) => product.duration),
        R.computed((product) => {
          const matrixLine = getPriceCardMatrixLine(product);
          return matrixLine?.residualValueVisible && product.residualValueMaxPercent > 0;
        })
      ),
      totalInterestPercent: R.when(
        R.propertyChanged((product) => product.duration),
        R.computed((product) => {
          const interestMinPercent = getInterestMinPercent(product);
          if (product.totalInterestPercent < interestMinPercent) return interestMinPercent;

          const interestMaxPercent = getInterestMaxPercent(product);
          if (product.totalInterestPercent > interestMaxPercent) return interestMaxPercent;

          return product.totalInterestPercent;
        })
      ),
      ratingBasedInterestPercent: R.when(
        R.propertyChanged((product) => [product.totalInterestPercent, product.duration]),
        R.computed((product) => {
          const ratingInterestPercentValue = getRatingBasedInterestPercent(product);
          return ratingInterestPercentValue;
        })
      ),
      interestPercent: R.when(
        R.propertyChanged((product) => [product.totalInterestPercent, product.duration]),
        R.computed((product) => {
          const matrixLine = getPriceCardMatrixLine(product);
          const customerInterestPercent =
            matrixLine && !matrixLine.interestUseSlider
              ? getCustomerInterestPercent(product, matrixLine)
              : product.totalInterestPercent;
          if (customerInterestPercent < product.interestMinPercent) return product.interestMinPercent;
          if (customerInterestPercent > product.interestMaxPercent) return product.interestMaxPercent;
          return customerInterestPercent;
        })
      ),
      flatRate: R.when(
        R.propertyChanged((product) => [product.totalInterestPercent, product.duration]),
        R.computed((product) => {
          return product.flatRate ? product.totalInterestPercent : null;
        })
      ),
      residualValueMaxPercentComputed: R.computed((product) => {
        if (product.residualValueIsReadonly) return product.residualValueMaxPercent;
        const matrixLine = getPriceCardMatrixLine(product);
        if (matrixLine?.residualValuePercent) {
          if (matrixLine.residualValuePercent > product.residualValueMaxPercent) return product.residualValueMaxPercent;
          return matrixLine.residualValuePercent;
        } else return product.residualValueMaxPercent;
      }),
      residualValueAmount:
        [
          R.computed((p) => {
            return getResidualValueDefaultAmount(p);
          }) |> R.when(R.propertiesChanged((p) => [p.duration])),
          R.computed((p) => {
            if (p.showAmountsWithVAT) return p.residualValueAmount;
            const residualValueMinAmount = (p.totalAmount * p.residualValueMinPercent) / 100;
            const residualValueMaxAmount = (p.totalAmount * p.residualValueMaxPercentComputed) / 100;
            const newAmount = (p.totalAmount * p.residualValuePercent) / 100 || 0;
            if (newAmount < residualValueMinAmount) return residualValueMinAmount;
            if (newAmount > residualValueMaxAmount) return residualValueMaxAmount;
            return newAmount;
          }) |> R.when(R.propertiesChanged((p) => [p.residualValuePercent])),
        ] |> R.chainRules,
      residualValueAmountWithVAT:
        [
          R.computed((p) => {
            return getResidualValueDefaultAmount(p);
          }) |> R.when(R.propertiesChanged((p) => [p.duration])),
          R.computed((p) => {
            if (!p.showAmountsWithVAT) return p.residualValueAmountWithVAT;
            const residualValueMinAmount = (p.totalAmountWithVAT * p.residualValueMinPercent) / 100;
            const residualValueMaxAmount = (p.totalAmountWithVAT * p.residualValueMaxPercentComputed) / 100;
            const newAmount = (p.totalAmountWithVAT * p.residualValuePercent) / 100 || 0;
            if (newAmount < residualValueMinAmount) return residualValueMinAmount;
            if (newAmount > residualValueMaxAmount) return residualValueMaxAmount;
            return newAmount;
          }) |> R.when(R.propertyChanged((p) => p.residualValuePercent)),
        ] |> R.chainRules,
      residualValuePercent:
        [
          R.computed((p) => {
            return getResidualValueDefaultPercent(p);
          }) |> R.when(R.propertiesChanged((p) => [p.duration])),
          R.computed((p) => {
            const customResidualValueAmount = p.showAmountsWithVAT
              ? p.residualValueAmountWithVAT
              : p.residualValueAmount;
            const customTotalAmount = p.showAmountsWithVAT ? p.totalAmountWithVAT : p.totalAmount;
            const newPercent = (100 * customResidualValueAmount) / customTotalAmount || 0;
            if (newPercent < p.residualValueMinPercent) return p.residualValueMinPercent;
            if (newPercent > p.residualValueMaxPercentComputed) return p.residualValueMaxPercentComputed;
            return newPercent;
          }) |> R.when(R.propertiesChanged((p) => [p.residualValueAmount, p.residualValueAmountWithVAT])),
        ] |> R.chainRules,
      downPaymentAmount:
        [
          R.computed((p) => {
            if (p.showAmountsWithVAT) return p.downPaymentAmount;
            const downPaymentMinAmount = (p.totalAmount * p.downPaymentMinPercent) / 100;
            const downPaymentMaxAmount = (p.totalAmount * p.downPaymentMaxPercent) / 100;
            const newAmount = (p.totalAmount * p.downPaymentPercent) / 100 || 0;
            if (newAmount < downPaymentMinAmount) return downPaymentMinAmount;
            if (newAmount > downPaymentMaxAmount) return downPaymentMaxAmount;
            return newAmount;
          }) |> R.when(R.propertiesChanged((p) => [p.downPaymentPercent])),
          R.minimumValue((p) => (p.totalAmount * p.downPaymentMinPercent) / 100),
          R.maximumValue((p) => (p.totalAmount * p.downPaymentMaxPercent) / 100),
        ] |> R.chainRules,
      downPaymentAmountWithVAT:
        R.computed((p) => {
          if (!p.showAmountsWithVAT) return p.downPaymentAmountWithVAT;
          const downPaymentMinAmount = (p.totalAmountWithVAT * p.downPaymentMinPercent) / 100;
          const downPaymentMaxAmount = (p.totalAmountWithVAT * p.downPaymentMaxPercent) / 100;
          const newAmount = (p.totalAmountWithVAT * p.downPaymentPercent) / 100 || 0;
          if (newAmount < downPaymentMinAmount) return downPaymentMinAmount;
          if (newAmount > downPaymentMaxAmount) return downPaymentMaxAmount;
          return newAmount;
        }) |> R.when(R.propertiesChanged((p) => [p.downPaymentPercent])),
      downPaymentPercent:
        R.computed((p) => {
          const customDownPaymentAmount = p.showAmountsWithVAT ? p.downPaymentAmountWithVAT : p.downPaymentAmount;
          const customTotalAmount = p.showAmountsWithVAT ? p.totalAmountWithVAT : p.totalAmount;
          const newPercent = (100 * customDownPaymentAmount) / customTotalAmount || 0;
          if (newPercent < p.downPaymentMinPercent) return p.downPaymentMinPercent;
          if (newPercent > p.downPaymentMaxPercent) return p.downPaymentMaxPercent;
          return newPercent;
        }) |> R.when(R.propertiesChanged((p) => [p.downPaymentAmount, p.downPaymentAmountWithVAT])),
      dealerCommissionValue: R.when(
        R.propertyChanged((product) => product.duration),
        R.computed(() => {
          return null;
        })
      ),
      subsidyValue: R.when(
        R.propertyChanged((product) => product.duration),
        R.computed(() => {
          return null;
        })
      ),
      cellParamsConfigurations: R.when(
        R.propertiesChanged((p) => [
          p.downPaymentPercent,
          p.cellParamsConfigurations,
          p.duration,
          p.residualValuePercent,
        ]),
        R.computed((p) => {
          if (!p.capitalPercentAutoCalculation) return p.cellParamsConfigurations;
          const capitalPercentCells = p.cellParamsConfigurations.reduce((count, x) => {
            if (x.params.some((y) => y.scheduleTypeCellParamCode === scheduleTypeCellParams.capitalPercentCode)) {
              count++;
            }
            return count;
          }, 0);

          p.cellParamsConfigurations.forEach((x) => {
            const paramToUpdate = x.params.find(
              (y) => y.scheduleTypeCellParamCode === scheduleTypeCellParams.capitalPercentCode
            );
            if (paramToUpdate) {
              paramToUpdate.value = (
                (100 - (p.downPaymentPercent ?? 0) - (p.residualValuePercent ?? 0)) /
                capitalPercentCells
              ).toString();
            }
          });

          return p.cellParamsConfigurations;
        })
      ),
      capitalPercentAutoCalculation: R.computed((p) => {
        if (p.capitalPercentAutoCalculation === undefined) return true;
        else return p.capitalPercentAutoCalculation;
      }),
    })
  );
};

export const getSettingsRules = (countryCode) =>
  R.shape({
    interestMinPercent:
      R.computed((settings) => {
        return getInterestMinPercent(settings.product);
      }) |> R.when(R.propertiesChanged((settings) => [settings.product.duration])),
    interestMaxPercent:
      R.computed((settings) => {
        return getInterestMaxPercent(settings.product);
      }) |> R.when(R.propertiesChanged((settings) => [settings.product.duration])),
    firstCellStartDate:
      R.computed((settings) => {
        if (!settings.product.firstInstallmentDate) return null;
        const firstInstallmentDate = new Date(settings.product.firstInstallmentDate);
        const date = new Date(
          firstInstallmentDate.getFullYear(),
          countryCode === countryCodeConstants.italy && settings.product.useInterestInArrears
            ? firstInstallmentDate.getMonth() + settings.product.periodicity
            : firstInstallmentDate.getMonth(),
          1
        );
        return date;
      }) |> R.when(R.propertyChanged((settings) => settings.product.firstInstallmentDate)),
    lastCellEndDate:
      R.computed((settings) => {
        if (!settings?.firstCellStartDate || !settings.product.duration) return null;
        const date = new Date(settings.firstCellStartDate.getFullYear(), settings.firstCellStartDate.getMonth(), 1);
        date.setMonth(settings.firstCellStartDate.getMonth() + settings.product.duration - 1);
        return new Date(
          date.getFullYear(),
          countryCode === countryCodeConstants.italy && settings.product.useInterestInArrears
            ? date.getMonth() + 1 - settings.product.periodicity
            : date.getMonth() + 1,
          0
        );
      })
      |> R.when(R.propertiesChanged((settings) => [settings.product.firstInstallmentDate, settings.product.duration])),
    years:
      R.computed((settings) => {
        if (!settings?.firstCellStartDate || !settings?.lastCellEndDate || !settings.product.duration) {
          return [];
        }
        const startYear =
          settings.firstCellStartDate.getFullYear() -
          (!!Number(settings.product.downPaymentAmount) && settings.firstCellStartDate.getMonth() === 0 ? 1 : 0);
        const endYear =
          settings.lastCellEndDate.getFullYear() +
          (settings.product.residualValueVisible && settings.lastCellEndDate.getMonth() === 11 ? 1 : 0);
        return range(startYear, endYear + 1).map((year) => ({
          name: year,
          months: months().map((name, index) => ({
            selected: false,
            year,
            index,
            name,
            date: new Date(year, index, 1),
          })),
        }));
      })
      |> R.when(
        R.propertiesChanged((settings) => {
          return [settings.firstCellStartDate, settings.lastCellEndDate, settings.product.duration];
        })
      ),
    cellParamConfigurations:
      R.computed(({ years, product }) => {
        let configs = null;
        years.forEach((y) =>
          y.months.forEach((m) => {
            if (!m.selected) return;

            const cellParamsConfiguration = product.cellParamsConfigurations?.find((x) =>
              moment(x.monthDate).isSame(m.date, "month")
            );
            const mConfigs = cellParamsConfiguration?.params || emptyArray;

            if (!configs) {
              configs = mConfigs;
            } else if (
              configs.length !== mConfigs.length ||
              !all(
                (x) =>
                  !!configs.find(
                    (y) => y.scheduleTypeCellParamCode === x.scheduleTypeCellParamCode && y.value === x.value
                  )
              )(mConfigs)
            ) {
              configs = [];
            }
          })
        );

        return configs;
      }) |> R.when(R.propertiesChanged((x) => [x.years, x.product.cellParamsConfigurations])),
    showMixedConfiguredOptionsWarning:
      R.computed((settings) => {
        const { years, cellParamConfigurations, product } = settings;
        return (
          cellParamConfigurations?.length === 0 &&
          years.some((year) =>
            year.months.some((month) => {
              const cellParamsConfiguration = product.cellParamsConfigurations?.find((x) =>
                moment(x.monthDate).isSame(month.date, "month")
              );
              return month.selected && cellParamsConfiguration?.params?.length > 0;
            })
          )
        );
      }) |> R.when(R.propertiesChanged((x) => [x.years, x.product.cellParamsConfigurations])),
    quoteProductServices:
      R.computed((settings) => {
        const { product, quoteProductServices, productServiceTypes } = settings;
        return (
          product.quoteProductServices &&
          product.quoteProductServices.map((service) => {
            const current = quoteProductServices?.find((x) => x.serviceTypeId === service.serviceTypeId) || {
              serviceTypeId: service.serviceTypeId,
              useServicePercent: true,
            };
            const amount = product.showAmountsWithVAT ? product.totalAmountWithVAT : product.totalAmount;
            return {
              ...current,
              readOnly: Boolean(productServiceTypes?.find((x) => x.serviceTypeId === service.serviceTypeId)?.readOnly),
              serviceMinAmount: getServiceMinAmount(amount, service, productServiceTypes),
              serviceMaxAmount: getServiceMaxAmount(amount, service, productServiceTypes),
              serviceMinPercent: getServiceMinPercent(amount, service, productServiceTypes),
              serviceMaxPercent: getServiceMaxPercent(amount, service, productServiceTypes),
            };
          })
        );
      }) |> R.when(R.propertiesChanged((x) => [x.productServiceTypes, x.product.quoteProductServices])),
    enableInterestSlider: R.computed((settings) => {
      const priceCardMatrixLine = getPriceCardMatrixLine(settings.product);
      return priceCardMatrixLine?.interestUseSlider || false;
    }),
    enableDealerSubsidyPreview: R.computed((settings) => {
      const priceCardMatrixLine = getPriceCardMatrixLine(settings.product);
      return priceCardMatrixLine?.enableDealerSubsidyPreview || false;
    }),
    enableDealerCommisionPreview: R.computed((settings) => {
      const priceCardMatrixLine = getPriceCardMatrixLine(settings.product);
      return priceCardMatrixLine?.enableDealerCommisionPreview || false;
    }),
    enableDownPaymentSlider: R.computed((settings) => {
      return settings.product.priceCard?.leasingFactorTypeCode !== leasingFactorTypeCodes.leasingFactorWithDP || false;
    }),
    residualValueReadOnly: R.computed((settings) => {
      return (
        (settings.product?.priceCard?.rvCalculationCode === rvCalculationCodeConstants.AOLRV &&
          !settings.product?.hasSchedule) ||
        false
      );
    }),
  });
